/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useQuery, useLazyQuery } from "@apollo/client";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import {
  GET_USER_MARKETPLACES,
  GET_USER_MARKETPLACES_NEXT,
  GET_USER_MARKETPLACES_PREV,
} from "graphql/query/admin";
import Table from "Components/Table/Table";

const STATUS_OPTIONS = ["AVAILABLE", "SOLD", "RENTED", "INACTIVE"];
const TYPE_OPTIONS = ["SELL", "RENT"];
const CATEGORY_OPTIONS = [
  "CAMERAS",
  "LENSES",
  "DRONES",
  "GIMBALS_STABILIZERS",
  "LIGHTING",
  "AUDIO_EQUIPMENT",
  "EDITING_MONITORING_EQUIPMENT",
  "BATTERIES_POWER",
  "STORAGE_MEMORY",
  "ACCESSORIES",
  "STUDIO_EQUIPMENT",
  "OTHERS",
];

const MarketplaceTab = ({ userId }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pageInfo, setPageInfo] = useState("");
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ category: "", status: "", type: "" });
  const [appliedFilters, setAppliedFilters] = useState({});

  const buildFilters = () => {
    const f = {};
    if (appliedFilters.category) f.category = appliedFilters.category;
    if (appliedFilters.status) f.status = appliedFilters.status;
    if (appliedFilters.type) f.type = appliedFilters.type;
    return f;
  };

  const { data, refetch, error, loading } = useQuery(GET_USER_MARKETPLACES, {
    variables: {
      userId,
      first: 10,
      orderBy: "createdAt_DESC",
      filters: buildFilters(),
    },
    fetchPolicy: "network-only",
    skip: !userId,
  });

  const [getNextPage, { loading: nextLoader }] = useLazyQuery(GET_USER_MARKETPLACES_NEXT);
  const [getPrevPage, { loading: prevLoader }] = useLazyQuery(GET_USER_MARKETPLACES_PREV);

  const mapEdges = (edges) =>
    edges.map((e) => ({
      ...e.node,
      locationPlace: e.node.location?.place || "N/A",
    }));

  const handleNextPage = async () => {
    if (pageInfo.hasNextPage) {
      const res = await getNextPage({
        variables: {
          userId,
          first: 10,
          after: pageInfo.endCursor,
          orderBy: "createdAt_DESC",
          filters: buildFilters(),
        },
        fetchPolicy: "network-only",
      });
      if (res.data?.userMarketplaces) {
        setItems(mapEdges(res.data.userMarketplaces.edges));
        setPage(page + 1);
        setPageInfo(res.data.userMarketplaces.pageInfo);
      }
    }
  };

  const handlePreviousPage = async () => {
    if (pageInfo.hasPreviousPage) {
      const res = await getPrevPage({
        variables: {
          userId,
          last: 10,
          before: pageInfo.startCursor,
          orderBy: "createdAt_DESC",
          filters: buildFilters(),
        },
        fetchPolicy: "network-only",
      });
      if (res.data?.userMarketplaces) {
        setItems(mapEdges(res.data.userMarketplaces.edges));
        setPage(page - 1);
        setPageInfo(res.data.userMarketplaces.pageInfo);
      }
    }
  };

  useEffect(() => {
    if (data?.userMarketplaces) {
      setItems(mapEdges(data.userMarketplaces.edges));
      setPageInfo(data.userMarketplaces.pageInfo);
    }
    setPage(0);
  }, [data]);

  const handleApplyFilter = () => {
    setAppliedFilters({ ...filters });
    setTimeout(() => refetch(), 100);
  };

  const handleResetFilter = () => {
    setFilters({ category: "", status: "", type: "" });
    setAppliedFilters({});
    setTimeout(() => refetch(), 100);
  };

  const handleViewButton = (id) => {
    const item = items.find((m) => m.id === id);
    navigate("/admin/user/view-marketplace", { state: { marketplaceId: id, userId, item } });
  };

  const TABLE_HEAD = [
    { id: "id", flag: "id", label: "No.", alignRight: false },
    { id: "productName", flag: "productName", label: "Product Name", alignRight: false },
    { id: "brand", flag: "brand", label: "Brand", alignRight: false },
    { id: "type", flag: "type", label: "Type", alignRight: false },
    { id: "category", flag: "category", label: "Category", alignRight: false },
    { id: "condition", flag: "condition", label: "Condition", alignRight: false },
    { id: "status", flag: "status", label: "Status", alignRight: false },
    { id: "price", flag: "price", label: "Price", alignRight: false },
    { id: "locationPlace", flag: "locationPlace", label: "Location", alignRight: false },
    { id: "createdAt", flag: "createdAt", label: "Created At", alignRight: false },
    {
      id: "actions",
      flag: { read: true },
      callback: { handleViewButton },
      label: "Actions",
      alignRight: false,
    },
  ];

  return (
    <Box style={{ padding: "16px 0" }}>
      {/* Filters */}
      <Grid container spacing={2} style={{ marginBottom: 16, padding: "0 8px" }}>
        <Grid item xs={12} sm={3}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {CATEGORY_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {TYPE_OPTIONS.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyFilter}
            style={{ background: "var(--color-primary, #1976d2)" }}
          >
            Apply
          </Button>
          <Button variant="outlined" onClick={handleResetFilter}>
            Reset
          </Button>
        </Grid>
      </Grid>

      <Table
        error={error}
        USERLIST={items}
        page={page}
        handleNextPage={handleNextPage}
        handlePreviousPage={handlePreviousPage}
        TABLE_HEAD={TABLE_HEAD}
        pageInfo={pageInfo}
        loading={loading || nextLoader || prevLoader}
      />
    </Box>
  );
};

export default MarketplaceTab;
